/*
 * Nilya coloring pages — the library and the editor.
 *
 * How coloring works: the line art is rasterised once, and every enclosed
 * area is found and numbered (a label per pixel). The black lines themselves
 * are then handed out to the nearest area, so a fill reaches right under the
 * line and no white halo is left at anti-aliased edges. Color lives on one
 * canvas, the lines on a transparent canvas laid over it, so nothing the user
 * does can ever paint over a line.
 *
 * Because areas are found from pixels, any line art works — generated SVG,
 * scanned PNG — with no per-page region data.
 */
(() => {
  const root = document.querySelector('[data-coloring]');
  if (!root) return;

  const catalog = JSON.parse(root.querySelector('[data-coloring-catalog]').textContent);
  const sheetsById = new Map(catalog.sheets.map((s) => [s.id, s]));
  const catsById = new Map(catalog.categories.map((c) => [c.id, c]));
  const LEVELS = { easy: 'Easy', medium: 'Medium', detailed: 'Detailed' };

  const $ = (sel, el = root) => el.querySelector(sel);
  const $$ = (sel, el = root) => [...el.querySelectorAll(sel)];
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const store = {
    get(key, fallback) {
      try {
        const v = localStorage.getItem('nc-' + key);
        return v === null ? fallback : JSON.parse(v);
      } catch {
        return fallback;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem('nc-' + key, JSON.stringify(value));
      } catch {}
    }
  };

  /* ── Saved progress (IndexedDB: the color layer is a PNG blob, too big and
        too binary for localStorage) ─────────────────────────────────────── */

  let dbPromise;
  function db() {
    dbPromise ||= new Promise((resolve, reject) => {
      const req = indexedDB.open('nilya-coloring', 1);
      req.onupgradeneeded = () => req.result.createObjectStore('progress', { keyPath: 'id' });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbPromise;
  }

  async function idb(mode, fn) {
    try {
      const d = await db();
      return await new Promise((resolve, reject) => {
        const tx = d.transaction('progress', mode);
        const req = fn(tx.objectStore('progress'));
        tx.oncomplete = () => resolve(req && req.result);
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      return undefined;
    }
  }

  const progress = new Map();

  async function loadProgressIndex() {
    const all = (await idb('readonly', (st) => st.getAll())) || [];
    for (const old of progress.values()) URL.revokeObjectURL(old.thumbUrl);
    progress.clear();
    for (const rec of all) {
      if (!sheetsById.has(rec.id)) continue;
      progress.set(rec.id, { updated: rec.updated, thumbUrl: URL.createObjectURL(rec.thumb) });
    }
  }

  /* ── Routing ─────────────────────────────────────────────────────────── */

  const KEYS = ['sheet', 'c', 's', 'q'];

  function readRoute() {
    const q = new URLSearchParams(location.search);
    return { sheet: q.get('sheet'), c: q.get('c'), s: q.get('s'), q: q.get('q') };
  }

  /* Only our own keys are touched. Anything else in the query — Shopify's
     ?view= preview switch, campaign tags — rides along untouched. */
  function hrefFor(params) {
    const url = new URL(location.href);
    for (const k of KEYS) url.searchParams.delete(k);
    for (const [k, v] of Object.entries(params)) if (v) url.searchParams.set(k, v);
    url.hash = '';
    return url.pathname + url.search;
  }

  /* A page whose handle is a category's slug shows that category; the hub
     page shows them all. Links go to a category's own page when it exists in
     the admin (catalog url), and fall back to ?c= on this page when not. */
  const home = catalog.home || null;

  function catHref(id, sub) {
    const c = catsById.get(id);
    if (id === home) return hrefFor({ s: sub });
    if (c.url) return c.url + (sub ? '?s=' + encodeURIComponent(sub) : '');
    return hrefFor({ c: id, s: sub });
  }

  function hubHref() {
    if (!home) return hrefFor({});
    return catalog.hubUrl || hrefFor({ c: 'all' });
  }

  function navigate(href, { replace = false } = {}) {
    const url = new URL(href, location.href);
    if (url.pathname !== location.pathname) {
      location.href = url.href;
      return;
    }
    if (replace) history.replaceState({ nc: true }, '', url.pathname + url.search);
    else history.pushState({ nc: true }, '', url.pathname + url.search);
    route();
  }

  let openedFromLibrary = false;

  function go(params, { replace = false } = {}) {
    const href = hrefFor(params);
    if (replace) history.replaceState({ nc: true }, '', href);
    else history.pushState({ nc: true }, '', href);
    route();
  }

  window.addEventListener('popstate', route);

  const lib = $('[data-lib]');
  const editorEl = $('[data-editor]');
  let level = store.get('level', 'all');

  function route() {
    const r = readRoute();
    if (r.sheet && sheetsById.has(r.sheet)) {
      openEditor(sheetsById.get(r.sheet));
      return;
    }
    closeEditor();
    const cat = r.c && catsById.has(r.c) ? r.c : r.c === 'all' ? null : home;
    if (r.q) renderSearch(r.q);
    else if (cat) renderCategory(catsById.get(cat), r.s);
    else renderHub();
    if (restoreScroll !== null) {
      window.scrollTo(0, restoreScroll);
      restoreScroll = null;
    }
  }

  /* ── Library ─────────────────────────────────────────────────────────── */

  function card(sheet) {
    const p = progress.get(sheet.id);
    const src = p ? p.thumbUrl : sheet.src;
    return `
      <li>
        <a class="nc-card" href="${esc(hrefFor({ sheet: sheet.id }))}" data-nav>
          <span class="nc-card__art${p ? ' is-started' : ''}">
            <img src="${esc(src)}" alt="${esc(sheet.name)} coloring page" loading="lazy" decoding="async" width="${sheet.w}" height="${sheet.h}">
            ${p ? '<span class="nc-card__badge">In progress</span>' : ''}
          </span>
          <span class="nc-card__name">${esc(sheet.name)}</span>
          <span class="nc-level nc-level--${sheet.level}">${LEVELS[sheet.level]}</span>
        </a>
      </li>`;
  }

  const byLevel = (list) => (level === 'all' ? list : list.filter((s) => s.level === level));

  function levelChips() {
    const opts = [['all', 'All levels'], ['easy', 'Easy'], ['medium', 'Medium'], ['detailed', 'Detailed']];
    return `<div class="nc-chips" role="group" aria-label="Difficulty">${opts
      .map(([v, l]) => `<button type="button" class="nc-chip" data-level="${v}" aria-pressed="${level === v}">${l}</button>`)
      .join('')}</div>`;
  }

  function toolbar(query = '') {
    return `
      <div class="nc-toolbar">
        <form class="nc-search" role="search" data-search>
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/></svg>
          <label class="visually-hidden" for="nc-q">Search coloring pages</label>
          <input id="nc-q" type="search" placeholder="Search: owl, snowflake, quilt…" value="${esc(query)}" autocomplete="off">
        </form>
        ${levelChips()}
        <button type="button" class="nc-btn nc-btn--ghost" data-random>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="8.5" cy="8.5" r="1.3"/><circle cx="15.5" cy="15.5" r="1.3"/><circle cx="12" cy="12" r="1.3"/></svg>
          Surprise me
        </button>
      </div>`;
  }

  function continueStrip() {
    const recent = [...progress.entries()].sort((a, b) => b[1].updated - a[1].updated).slice(0, 6);
    if (!recent.length) return '';
    return `
      <section class="nc-continue" aria-labelledby="nc-continue-title">
        <h2 class="nc-h2" id="nc-continue-title">Pick up where you left off</h2>
        <ul class="nc-grid nc-grid--compact">${recent.map(([id]) => card(sheetsById.get(id))).join('')}</ul>
      </section>`;
  }

  function renderHub() {
    const cats = catalog.categories
      .map((c) => {
        const list = catalog.sheets.filter((s) => s.cat === c.id);
        const covers = list.slice(0, 3);
        return `
          <li>
            <a class="nc-cat" href="${esc(catHref(c.id))}" data-nav>
              <span class="nc-cat__art">
                ${covers.map((s, i) => `<img class="nc-cat__img nc-cat__img--${i}" src="${esc(s.src)}" alt="${i ? '' : esc(c.title)}" loading="lazy" decoding="async" width="${s.w}" height="${s.h}">`).join('')}
              </span>
              <span class="nc-cat__text">
                <span class="nc-cat__name">${esc(c.name)}</span>
                <span class="nc-cat__count">${list.length} pages</span>
              </span>
            </a>
          </li>`;
      })
      .join('');
    lib.innerHTML = `
      ${toolbar()}
      ${continueStrip()}
      <section aria-labelledby="nc-cats-title">
        <h2 class="nc-h2" id="nc-cats-title">Choose a category</h2>
        <ul class="nc-cats">${cats}</ul>
      </section>`;
    setTitle(home ? 'All' : '');
  }

  function crumbs(items) {
    return `<nav class="nc-crumbs" aria-label="Breadcrumb"><ol>${items
      .map(([label, href], i) => (i === items.length - 1 ? `<li aria-current="page">${esc(label)}</li>` : `<li><a href="${esc(href)}" data-nav>${esc(label)}</a></li>`))
      .join('')}</ol></nav>`;
  }

  function renderCategory(cat, subId) {
    const sub = cat.subs.find((x) => x.id === subId);
    const all = catalog.sheets.filter((s) => s.cat === cat.id);
    const list = byLevel(sub ? all.filter((s) => s.sub === sub.id) : all);
    const subs = cat.subs.length
      ? `<div class="nc-chips nc-chips--subs" role="group" aria-label="${esc(cat.name)} collections">
          <a class="nc-chip" href="${esc(catHref(cat.id))}" data-nav aria-current="${sub ? 'false' : 'page'}">All ${esc(cat.name.toLowerCase())}</a>
          ${cat.subs.map((x) => `<a class="nc-chip" href="${esc(catHref(cat.id, x.id))}" data-nav aria-current="${sub && sub.id === x.id ? 'page' : 'false'}">${esc(x.name)}</a>`).join('')}
        </div>`
      : '';
    lib.innerHTML = `
      ${toolbar()}
      ${crumbs([['All categories', hubHref()], [cat.name, catHref(cat.id)], ...(sub ? [[sub.name, '']] : [])])}
      ${cat.id === home && !sub ? '' : `<div class="nc-cat-head">
        <h2 class="nc-h2 nc-h2--big">${esc(sub ? sub.name : cat.name)}</h2>
        <p class="nc-cat-head__blurb">${esc(cat.blurb)}</p>
      </div>`}
      ${subs}
      ${list.length ? `<ul class="nc-grid">${list.map(card).join('')}</ul>` : `<p class="nc-empty">No ${esc(LEVELS[level] || '').toLowerCase()} pages here yet — try another level.</p>`}`;
    setTitle(sub ? `${sub.name} — ${cat.name}` : cat.id === home ? '' : cat.name);
  }

  function renderSearch(q) {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const hits = byLevel(
      catalog.sheets.filter((s) => {
        const cat = catsById.get(s.cat);
        const sub = cat.subs.find((x) => x.id === s.sub);
        const hay = [s.name, cat.name, sub && sub.name, ...(s.tags || [])].join(' ').toLowerCase();
        return words.every((w) => hay.includes(w));
      })
    );
    lib.innerHTML = `
      ${toolbar(q)}
      ${crumbs([['All categories', hubHref()], [`Search: ${q}`, '']])}
      <h2 class="nc-h2">${hits.length} ${hits.length === 1 ? 'page' : 'pages'} for “${esc(q)}”</h2>
      ${hits.length ? `<ul class="nc-grid">${hits.map(card).join('')}</ul>` : '<p class="nc-empty">Nothing matched. Try a simpler word, like “flower” or “star”.</p>'}`;
    setTitle(`Search: ${q}`);
  }

  const baseTitle = document.title;
  function setTitle(prefix) {
    document.title = prefix ? `${prefix} coloring pages — ${baseTitle}` : baseTitle;
  }

  lib.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-nav]');
    if (a && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      const url = new URL(a.href);
      if (url.pathname !== location.pathname) return;
      e.preventDefault();
      openedFromLibrary = url.searchParams.has('sheet');
      history.pushState({ nc: true }, '', url.pathname + url.search);
      route();
      if (!openedFromLibrary) lib.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }
    const chip = e.target.closest('[data-level]');
    if (chip) {
      level = chip.dataset.level;
      store.set('level', level);
      route();
      return;
    }
    if (e.target.closest('[data-random]')) {
      const pool = byLevel(catalog.sheets);
      const pick = pool[Math.floor(Math.random() * pool.length)];
      openedFromLibrary = true;
      go({ sheet: pick.id });
    }
  });

  let searchTimer;
  lib.addEventListener('input', (e) => {
    if (e.target.id !== 'nc-q') return;
    clearTimeout(searchTimer);
    const value = e.target.value.trim();
    searchTimer = setTimeout(() => {
      const r = readRoute();
      history.replaceState({ nc: true }, '', hrefFor(value ? { q: value } : r.q ? {} : { c: r.c, s: r.s }));
      const pos = e.target.selectionStart;
      route();
      const input = $('#nc-q');
      input.focus();
      input.setSelectionRange(pos, pos);
    }, 250);
  });
  lib.addEventListener('submit', (e) => {
    if (e.target.matches('[data-search]')) e.preventDefault();
  });

  /* ── Fill styles ─────────────────────────────────────────────────────── */

  const PATTERNS = {
    dots(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.fillStyle = b;
      for (const [x, y] of [[T * 0.25, T * 0.25], [T * 0.75, T * 0.75]]) {
        c.beginPath();
        c.arc(x, y, T * 0.14, 0, Math.PI * 2);
        c.fill();
      }
    },
    stripes(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.strokeStyle = b;
      c.lineWidth = T * 0.22;
      for (const o of [-T, 0, T]) {
        c.beginPath();
        c.moveTo(o, T);
        c.lineTo(o + T, 0);
        c.stroke();
      }
    },
    checks(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.fillStyle = b;
      c.fillRect(0, 0, T / 2, T / 2);
      c.fillRect(T / 2, T / 2, T / 2, T / 2);
    },
    chevron(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.strokeStyle = b;
      c.lineWidth = T * 0.16;
      c.lineJoin = 'miter';
      for (const o of [-T, 0, T]) {
        c.beginPath();
        c.moveTo(0, T * 0.7 + o);
        c.lineTo(T / 2, T * 0.2 + o);
        c.lineTo(T, T * 0.7 + o);
        c.stroke();
      }
    },
    waves(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.strokeStyle = b;
      c.lineWidth = T * 0.13;
      c.beginPath();
      for (let x = 0; x <= T; x += 1) {
        const y = T / 2 + Math.sin((x / T) * Math.PI * 2) * T * 0.2;
        x ? c.lineTo(x, y) : c.moveTo(x, y);
      }
      c.stroke();
    },
    stars(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.fillStyle = b;
      const starAt = (x, y, r) => {
        c.beginPath();
        for (let i = 0; i < 10; i++) {
          const ang = -Math.PI / 2 + (i * Math.PI) / 5;
          const rr = i % 2 ? r * 0.45 : r;
          c.lineTo(x + Math.cos(ang) * rr, y + Math.sin(ang) * rr);
        }
        c.closePath();
        c.fill();
      };
      starAt(T * 0.27, T * 0.3, T * 0.2);
      starAt(T * 0.75, T * 0.76, T * 0.15);
    },
    hearts(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.fillStyle = b;
      const heart = (x, y, s) => {
        c.beginPath();
        c.moveTo(x, y + s * 0.35);
        c.bezierCurveTo(x - s * 0.6, y - s * 0.1, x - s * 0.3, y - s * 0.55, x, y - s * 0.2);
        c.bezierCurveTo(x + s * 0.3, y - s * 0.55, x + s * 0.6, y - s * 0.1, x, y + s * 0.35);
        c.fill();
      };
      heart(T * 0.28, T * 0.3, T * 0.36);
      heart(T * 0.75, T * 0.78, T * 0.3);
    },
    plaid(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.globalAlpha = 0.5;
      c.fillStyle = b;
      c.fillRect(T * 0.15, 0, T * 0.3, T);
      c.fillRect(0, T * 0.15, T, T * 0.3);
      c.globalAlpha = 1;
      c.fillRect(T * 0.72, 0, T * 0.05, T);
      c.fillRect(0, T * 0.72, T, T * 0.05);
    },
    scales(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      c.strokeStyle = b;
      c.lineWidth = T * 0.09;
      for (const [x, y] of [[0, 0], [T, 0], [T / 2, T / 2], [0, T], [T, T]]) {
        c.beginPath();
        c.arc(x, y, T / 2, 0, Math.PI);
        c.stroke();
      }
    },
    glitter(c, T, a, b) {
      c.fillStyle = a;
      c.fillRect(0, 0, T, T);
      const n = Math.round(T * T * 0.05);
      for (let i = 0; i < n; i++) {
        const r = Math.random();
        c.globalAlpha = 0.35 + Math.random() * 0.65;
        c.fillStyle = r < 0.45 ? b : r < 0.75 ? '#ffffff' : 'rgba(0,0,0,0.35)';
        const s = 1 + Math.random() * T * 0.04;
        c.fillRect(Math.random() * T, Math.random() * T, s, s);
      }
      c.globalAlpha = 1;
    }
  };
  const PATTERN_NAMES = { dots: 'Dots', stripes: 'Stripes', checks: 'Checks', chevron: 'Zigzag', waves: 'Waves', stars: 'Stars', hearts: 'Hearts', plaid: 'Plaid', scales: 'Scales', glitter: 'Glitter' };

  function patternTile(kind, a, b, scale, base) {
    const T = Math.max(6, Math.round((kind === 'glitter' ? base * 4 : base) * scale));
    const c = document.createElement('canvas');
    c.width = c.height = T;
    PATTERNS[kind](c.getContext('2d'), T, a, b);
    return c;
  }

  /* ── Palettes ────────────────────────────────────────────────────────── */

  const PALETTES = {
    Classic: ['#d62828', '#f25c54', '#f77f00', '#fcbf49', '#ffe066', '#a7c957', '#38b000', '#1b7f3b', '#2ec4b6', '#0096c7', '#1d4e89', '#3a0ca3', '#7b2cbf', '#c77dff', '#ff70a6', '#ffb3c6', '#8d5524', '#c68b59', '#e9c46a', '#ffffff', '#adb5bd', '#6c757d', '#343a40', '#111111'],
    Pastel: ['#ffadad', '#ffd6a5', '#fdffb6', '#caffbf', '#9bf6ff', '#a0c4ff', '#bdb2ff', '#ffc6ff', '#fcd5ce', '#ffe5d9', '#f8edeb', '#d8e2dc', '#cdeac0', '#b8e0d2', '#d6eadf', '#ece4db', '#e8e8e4', '#fffffc'],
    Bright: ['#ff006e', '#f72585', '#ef476f', '#e71d36', '#fb5607', '#ff9f1c', '#ffbe0b', '#ffd166', '#80ed99', '#06d6a0', '#2ec4b6', '#4cc9f0', '#118ab2', '#3a86ff', '#8338ec', '#7209b7', '#073b4c', '#011627'],
    Earth: ['#fefae0', '#e9edc9', '#ccd5ae', '#606c38', '#283618', '#dda15e', '#bc6c25', '#d4a373', '#c9a227', '#ede0d4', '#ddb892', '#b08968', '#9c6644', '#7f5539', '#6b4226', '#a47148', '#3d405b', '#8b5e3c'],
    Ocean: ['#03045e', '#023e8a', '#0077b6', '#0096c7', '#00b4d8', '#48cae4', '#90e0ef', '#ade8f4', '#caf0f8', '#2a9d8f', '#264653', '#e9c46a', '#f4a261', '#e76f51', '#ffffff'],
    'Winter & holiday': ['#9b2226', '#ae2012', '#bb3e03', '#ca6702', '#ee9b00', '#e9d8a6', '#94d2bd', '#0a9396', '#005f73', '#001219', '#2d6a4f', '#40916c', '#d8f3dc', '#caf0f8', '#ffffff'],
    'Skin & hair': ['#fff1e0', '#ffe0bd', '#ffcd94', '#f1c27d', '#eac086', '#e0ac69', '#e6be8a', '#d1a37a', '#c68642', '#a0662f', '#8d5524', '#b55239', '#5c3a21', '#3b2219', '#2b1b17'],
    Grays: ['#ffffff', '#f1f3f5', '#dee2e6', '#ced4da', '#adb5bd', '#868e96', '#495057', '#343a40', '#212529', '#000000']
  };

  /* ── Board: rasterise, segment, paint ────────────────────────────────── */

  const INK = [27, 27, 27];

  function canvas(w, h, opts) {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return [c, c.getContext('2d', opts)];
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not load ' + src));
      img.src = src;
    });
  }

  /*
   * Label every enclosed area. Returns the per-pixel labels plus, for each
   * area, its bounding box and the list of its pixels (as one array sorted by
   * label, with offsets) so a fill touches only its own pixels.
   */
  function segment(rgba, W, H) {
    const N = W * H;
    const lab = new Int32Array(N);
    const isLine = new Uint8Array(N);
    for (let i = 0, j = 0; i < N; i++, j += 4) {
      isLine[i] = rgba[j] * 0.299 + rgba[j + 1] * 0.587 + rgba[j + 2] * 0.114 < 190 ? 1 : 0;
    }

    const stack = new Int32Array(N);
    const sizes = [0];
    let next = 1;
    for (let i = 0; i < N; i++) {
      if (isLine[i] || lab[i]) continue;
      let sp = 0;
      let size = 0;
      stack[sp++] = i;
      lab[i] = next;
      while (sp) {
        const p = stack[--sp];
        size++;
        const x = p % W;
        if (x > 0 && !lab[p - 1] && !isLine[p - 1]) { lab[p - 1] = next; stack[sp++] = p - 1; }
        if (x < W - 1 && !lab[p + 1] && !isLine[p + 1]) { lab[p + 1] = next; stack[sp++] = p + 1; }
        if (p >= W && !lab[p - W] && !isLine[p - W]) { lab[p - W] = next; stack[sp++] = p - W; }
        if (p < N - W && !lab[p + W] && !isLine[p + W]) { lab[p + W] = next; stack[sp++] = p + W; }
      }
      sizes.push(size);
      next++;
    }

    /* Specks smaller than this are anti-aliasing debris between strokes, not
       areas anyone could aim at; they are folded into the lines. */
    const minArea = Math.max(24, N * 0.000012);
    const remap = new Int32Array(next);
    let count = 0;
    for (let k = 1; k < next; k++) remap[k] = sizes[k] >= minArea ? ++count : 0;
    for (let i = 0; i < N; i++) lab[i] = remap[lab[i]];

    /* Hand each line pixel to the nearest area, breadth first. */
    let head = 0;
    let tail = 0;
    const queue = stack;
    for (let i = 0; i < N; i++) {
      if (!lab[i]) continue;
      const x = i % W;
      if ((x > 0 && !lab[i - 1]) || (x < W - 1 && !lab[i + 1]) || (i >= W && !lab[i - W]) || (i < N - W && !lab[i + W])) queue[tail++] = i;
    }
    while (head < tail) {
      const p = queue[head++];
      const l = lab[p];
      const x = p % W;
      if (x > 0 && !lab[p - 1]) { lab[p - 1] = l; queue[tail++] = p - 1; }
      if (x < W - 1 && !lab[p + 1]) { lab[p + 1] = l; queue[tail++] = p + 1; }
      if (p >= W && !lab[p - W]) { lab[p - W] = l; queue[tail++] = p - W; }
      if (p < N - W && !lab[p + W]) { lab[p + W] = l; queue[tail++] = p + W; }
    }

    const R = count + 1;
    const start = new Int32Array(R + 1);
    const minX = new Int32Array(R).fill(W);
    const minY = new Int32Array(R).fill(H);
    const maxX = new Int32Array(R).fill(-1);
    const maxY = new Int32Array(R).fill(-1);
    for (let i = 0; i < N; i++) {
      const l = lab[i];
      start[l + 1]++;
      const x = i % W;
      const y = (i / W) | 0;
      if (x < minX[l]) minX[l] = x;
      if (x > maxX[l]) maxX[l] = x;
      if (y < minY[l]) minY[l] = y;
      if (y > maxY[l]) maxY[l] = y;
    }
    for (let k = 1; k <= R; k++) start[k] += start[k - 1];
    const order = new Int32Array(N);
    const fillAt = start.slice(0, R);
    for (let i = 0; i < N; i++) order[fillAt[lab[i]]++] = i;
    return { lab, start, order, minX, minY, maxX, maxY, regions: count };
  }

  class Board {
    constructor(host) {
      this.host = host;
      this.undo = [];
      this.redo = [];
      this.masks = new Map();
      this.pending = false;
    }

    async load(sheet, saved) {
      const img = await loadImage(sheet.src);
      const touch = matchMedia('(pointer: coarse)').matches;
      const max = touch ? 1400 : 1600;
      const k = max / Math.max(sheet.w, sheet.h);
      const W = (this.W = Math.round(sheet.w * k));
      const H = (this.H = Math.round(sheet.h * k));

      const [src, sctx] = canvas(W, H, { willReadFrequently: true });
      sctx.fillStyle = '#fff';
      sctx.fillRect(0, 0, W, H);
      sctx.drawImage(img, 0, 0, W, H);
      const rgba = sctx.getImageData(0, 0, W, H).data;
      this.seg = segment(rgba, W, H);

      const [line, lctx] = canvas(W, H);
      const lineData = lctx.createImageData(W, H);
      const d = lineData.data;
      const lineA = new Uint8Array(W * H);
      for (let j = 0, i = 0; j < d.length; j += 4, i++) {
        const lum = rgba[j] * 0.299 + rgba[j + 1] * 0.587 + rgba[j + 2] * 0.114;
        d[j] = INK[0];
        d[j + 1] = INK[1];
        d[j + 2] = INK[2];
        d[j + 3] = lineA[i] = lum > 246 ? 0 : Math.min(255, (255 - lum) * 1.12);
      }
      this.lineA = lineA;
      lctx.putImageData(lineData, 0, 0);

      const [color, cctx] = canvas(W, H, { willReadFrequently: true });
      cctx.fillStyle = '#fff';
      cctx.fillRect(0, 0, W, H);
      if (saved) {
        try {
          const bmp = await createImageBitmap(saved.color);
          cctx.drawImage(bmp, 0, 0, W, H);
        } catch {}
      }
      const [before, bctx] = canvas(W, H, { willReadFrequently: true });

      this.color = color;
      this.cctx = cctx;
      this.line = line;
      this.before = before;
      this.bctx = bctx;
      this.undo = [];
      this.redo = [];
      this.masks.clear();
      this.touched = false;

      color.className = 'nc-layer';
      line.className = 'nc-layer nc-layer--lines';
      color.setAttribute('aria-label', `${sheet.name} — coloring area`);
      color.setAttribute('role', 'img');
      line.setAttribute('aria-hidden', 'true');
      this.host.replaceChildren(color, line);
      this.host.style.width = W + 'px';
      this.host.style.height = H + 'px';
    }

    labelAt(x, y) {
      x = Math.floor(x);
      y = Math.floor(y);
      if (x < 0 || y < 0 || x >= this.W || y >= this.H) return 0;
      return this.seg.lab[y * this.W + x];
    }

    mask(r) {
      let m = this.masks.get(r);
      if (m) {
        this.masks.delete(r);
        this.masks.set(r, m);
        return m;
      }
      const { start, order, minX, minY, maxX, maxY } = this.seg;
      const x0 = minX[r];
      const y0 = minY[r];
      const w = maxX[r] - x0 + 1;
      const h = maxY[r] - y0 + 1;
      const [c, ctx] = canvas(w, h);
      const img = ctx.createImageData(w, h);
      const a = img.data;
      const W = this.W;
      for (let k = start[r]; k < start[r + 1]; k++) {
        const i = order[k];
        a[((((i / W) | 0) - y0) * w + ((i % W) - x0)) * 4 + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      m = { c, x0, y0, w, h };
      this.masks.set(r, m);
      if (this.masks.size > 24) this.masks.delete(this.masks.keys().next().value);
      return m;
    }

    begin() {
      if (this.pending) return;
      this.bctx.drawImage(this.color, 0, 0);
      this.dirty = null;
      this.pending = true;
    }

    mark(x, y, w, h) {
      const x1 = Math.max(0, Math.floor(x));
      const y1 = Math.max(0, Math.floor(y));
      const x2 = Math.min(this.W, Math.ceil(x + w));
      const y2 = Math.min(this.H, Math.ceil(y + h));
      if (x2 <= x1 || y2 <= y1) return;
      const d = this.dirty;
      this.dirty = d ? { x1: Math.min(d.x1, x1), y1: Math.min(d.y1, y1), x2: Math.max(d.x2, x2), y2: Math.max(d.y2, y2) } : { x1, y1, x2, y2 };
    }

    commit() {
      if (!this.pending) return false;
      this.pending = false;
      const d = this.dirty;
      if (!d) return false;
      const w = d.x2 - d.x1;
      const h = d.y2 - d.y1;
      this.undo.push({
        x: d.x1,
        y: d.y1,
        before: this.bctx.getImageData(d.x1, d.y1, w, h),
        after: this.cctx.getImageData(d.x1, d.y1, w, h)
      });
      this.redo = [];
      /* Keep history inside roughly 160 MB of pixels, but never fewer than a
         dozen steps. */
      let bytes = this.undo.reduce((n, u) => n + u.before.data.length * 2, 0);
      while (this.undo.length > 12 && bytes > 160e6) bytes -= this.undo.shift().before.data.length * 2;
      this.touched = true;
      return true;
    }

    abort() {
      if (!this.pending) return;
      this.pending = false;
      const d = this.dirty;
      if (d) this.cctx.drawImage(this.before, d.x1, d.y1, d.x2 - d.x1, d.y2 - d.y1, d.x1, d.y1, d.x2 - d.x1, d.y2 - d.y1);
    }

    step(from, to, key) {
      const u = from.pop();
      if (!u) return false;
      this.cctx.putImageData(u[key], u.x, u.y);
      to.push(u);
      this.touched = true;
      return true;
    }

    fill(r, style) {
      if (!r) return;
      const m = this.mask(r);
      const [t, tc] = canvas(m.w, m.h);
      tc.fillStyle = this.fillStyle(tc, style, m);
      tc.fillRect(0, 0, m.w, m.h);
      tc.globalCompositeOperation = 'destination-in';
      tc.drawImage(m.c, 0, 0);
      this.cctx.drawImage(t, m.x0, m.y0);
      this.mark(m.x0, m.y0, m.w, m.h);
    }

    fillStyle(ctx, { mode, a, b, pattern, scale }, m) {
      if (mode === 'linear') {
        const g = ctx.createLinearGradient(0, 0, m.w * 0.25, m.h);
        g.addColorStop(0, a);
        g.addColorStop(1, b);
        return g;
      }
      if (mode === 'radial') {
        const g = ctx.createRadialGradient(m.w / 2, m.h / 2, 0, m.w / 2, m.h / 2, Math.max(m.w, m.h) * 0.62);
        g.addColorStop(0, a);
        g.addColorStop(1, b);
        return g;
      }
      if (mode === 'pattern') {
        const p = ctx.createPattern(patternTile(pattern, a, b, scale, this.W / 50), 'repeat');
        /* Anchor to the page, not the area, so neighbouring areas filled with
           the same pattern line up as one cloth. */
        p.setTransform(new DOMMatrix().translate(-m.x0, -m.y0));
        return p;
      }
      return a;
    }

    /* One segment of a brush stroke, clipped to `m` (an area mask) if given. */
    paint(ax, ay, bx, by, brush, m) {
      const r = brush.size / 2 + 2;
      let x0 = Math.floor(Math.min(ax, bx) - r);
      let y0 = Math.floor(Math.min(ay, by) - r);
      let x1 = Math.ceil(Math.max(ax, bx) + r);
      let y1 = Math.ceil(Math.max(ay, by) + r);
      if (m) {
        x0 = Math.max(x0, m.x0);
        y0 = Math.max(y0, m.y0);
        x1 = Math.min(x1, m.x0 + m.w);
        y1 = Math.min(y1, m.y0 + m.h);
      }
      x0 = Math.max(0, x0);
      y0 = Math.max(0, y0);
      x1 = Math.min(this.W, x1);
      y1 = Math.min(this.H, y1);
      const w = x1 - x0;
      const h = y1 - y0;
      if (w <= 0 || h <= 0) return;
      const [t, tc] = scratch(w, h);
      tc.translate(-x0, -y0);
      const color = brush.eraser ? '#ffffff' : brush.color;
      if (brush.kind === 'air' && !brush.eraser) {
        const dist = Math.hypot(bx - ax, by - ay);
        const stepLen = Math.max(1, brush.size * 0.12);
        const n = Math.max(1, Math.ceil(dist / stepLen));
        const rr = brush.size / 2;
        for (let i = 1; i <= n; i++) {
          const x = ax + ((bx - ax) * i) / n;
          const y = ay + ((by - ay) * i) / n;
          const g = tc.createRadialGradient(x, y, 0, x, y, rr);
          g.addColorStop(0, hexA(color, 0.09));
          g.addColorStop(1, hexA(color, 0));
          tc.fillStyle = g;
          tc.fillRect(x - rr, y - rr, rr * 2, rr * 2);
        }
      } else {
        tc.strokeStyle = brush.kind === 'crayon' && !brush.eraser ? crayon(color, tc) : color;
        tc.fillStyle = tc.strokeStyle;
        tc.lineWidth = brush.size;
        tc.lineCap = 'round';
        tc.lineJoin = 'round';
        tc.beginPath();
        if (ax === bx && ay === by) {
          tc.arc(ax, ay, brush.size / 2, 0, Math.PI * 2);
          tc.fill();
        } else {
          tc.moveTo(ax, ay);
          tc.lineTo(bx, by);
          tc.stroke();
        }
      }
      tc.setTransform(1, 0, 0, 1, 0, 0);
      if (m) {
        tc.globalCompositeOperation = 'destination-in';
        tc.drawImage(m.c, m.x0 - x0, m.y0 - y0);
        tc.globalCompositeOperation = 'source-over';
      }
      this.cctx.drawImage(t, 0, 0, w, h, x0, y0, w, h);
      this.mark(x0, y0, w, h);
    }

    /* The color as seen: paint underneath, blended with the line on top, so
       tapping a black line picks black rather than the paint hidden under it. */
    pick(x, y) {
      const px = clamp(Math.floor(x), 0, this.W - 1);
      const py = clamp(Math.floor(y), 0, this.H - 1);
      const d = this.cctx.getImageData(px, py, 1, 1).data;
      const a = this.lineA[py * this.W + px] / 255;
      return '#' + [0, 1, 2].map((k) => Math.round(d[k] * (1 - a) + INK[k] * a).toString(16).padStart(2, '0')).join('');
    }

    clear() {
      this.begin();
      this.cctx.fillStyle = '#fff';
      this.cctx.fillRect(0, 0, this.W, this.H);
      this.mark(0, 0, this.W, this.H);
      this.commit();
    }

    composite(maxSide, credit) {
      const k = maxSide ? Math.min(1, maxSide / Math.max(this.W, this.H)) : 1;
      const w = Math.round(this.W * k);
      const h = Math.round(this.H * k);
      const strip = credit ? Math.round(h * 0.045) : 0;
      const [c, ctx] = canvas(w, h + strip);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h + strip);
      ctx.drawImage(this.color, 0, 0, w, h);
      ctx.drawImage(this.line, 0, 0, w, h);
      if (credit) {
        ctx.fillStyle = '#5a6875';
        ctx.font = `600 ${Math.round(strip * 0.42)}px Karla, system-ui, sans-serif`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(credit, w - strip * 0.6, h + strip / 2);
      }
      return c;
    }
  }

  /* One reusable scratch canvas for brush segments — they run at pointer
     rate, and allocating a canvas per event is what makes strokes stutter. */
  let scratchC;
  let scratchX;
  function scratch(w, h) {
    if (!scratchC || scratchC.width < w || scratchC.height < h) {
      [scratchC, scratchX] = canvas(Math.max(w, scratchC ? scratchC.width : 0), Math.max(h, scratchC ? scratchC.height : 0));
    }
    scratchX.setTransform(1, 0, 0, 1, 0, 0);
    scratchX.globalCompositeOperation = 'source-over';
    scratchX.clearRect(0, 0, w, h);
    return [scratchC, scratchX];
  }

  function hexA(hex, alpha) {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
  }

  const crayonCache = new Map();
  function crayon(color, ctx) {
    let tile = crayonCache.get(color);
    if (!tile) {
      const [c, x] = canvas(96, 96);
      const img = x.createImageData(96, 96);
      const n = parseInt(color.slice(1), 16);
      for (let j = 0; j < img.data.length; j += 4) {
        img.data[j] = (n >> 16) & 255;
        img.data[j + 1] = (n >> 8) & 255;
        img.data[j + 2] = n & 255;
        const r = Math.random();
        img.data[j + 3] = r < 0.62 ? 150 + Math.random() * 105 : Math.random() * 70;
      }
      x.putImageData(img, 0, 0);
      tile = c;
      crayonCache.set(color, tile);
      if (crayonCache.size > 30) crayonCache.delete(crayonCache.keys().next().value);
    }
    return ctx.createPattern(tile, 'repeat');
  }

  /* ── Editor ──────────────────────────────────────────────────────────── */

  const stage = $('[data-stage]', editorEl);
  const boardEl = $('[data-board]', editorEl);
  const cursorEl = $('[data-cursor]', editorEl);
  const loadingEl = $('[data-loading]', editorEl);
  const zoomEl = $('[data-zoom]', editorEl);
  const board = new Board(boardEl);

  const state = {
    sheet: null,
    tool: 'fill',
    slot: 0,
    colors: store.get('colors', ['#e4572e', '#ffd166']),
    fill: store.get('fill', 'solid'),
    pattern: store.get('pattern', 'dots'),
    patternScale: 1,
    brush: store.get('brush', 'marker'),
    size: store.get('size', 28),
    inside: true,
    sym: 1,
    palette: store.get('palette', 'Classic'),
    recent: store.get('recent', [])
  };
  if (!PALETTES[state.palette]) state.palette = 'Classic';

  const view = { s: 1, tx: 0, ty: 0, fitS: 1, fitted: true };

  function applyView() {
    boardEl.style.transform = `translate(${view.tx}px, ${view.ty}px) scale(${view.s})`;
    zoomEl.textContent = Math.round((view.s / view.fitS) * 100) + '%';
  }

  function fit() {
    if (!board.W) return;
    const r = stage.getBoundingClientRect();
    const pad = r.width < 500 ? 8 : 20;
    view.fitS = Math.min((r.width - pad * 2) / board.W, (r.height - pad * 2) / board.H);
    view.s = view.fitS;
    view.tx = (r.width - board.W * view.s) / 2;
    view.ty = (r.height - board.H * view.s) / 2;
    view.fitted = true;
    applyView();
  }

  function zoomAt(factor, cx, cy) {
    const ns = clamp(view.s * factor, view.fitS * 0.6, view.fitS * 14);
    view.tx = cx - (cx - view.tx) * (ns / view.s);
    view.ty = cy - (cy - view.ty) * (ns / view.s);
    view.s = ns;
    view.fitted = false;
    applyView();
  }

  function zoomCenter(factor) {
    const r = stage.getBoundingClientRect();
    zoomAt(factor, r.width / 2, r.height / 2);
  }

  new ResizeObserver(() => {
    if (!editorEl.hidden && view.fitted) fit();
  }).observe(stage);

  function toBoard(e) {
    const r = stage.getBoundingClientRect();
    return [(e.clientX - r.left - view.tx) / view.s, (e.clientY - r.top - view.ty) / view.s];
  }

  /* Symmetry turns one point into n, rotated about the page centre. */
  function mirrored(x, y) {
    const n = state.sym;
    if (n <= 1) return [[x, y]];
    const cx = board.W / 2;
    const cy = board.H / 2;
    const out = [];
    for (let i = 0; i < n; i++) {
      const a = (i * Math.PI * 2) / n;
      const c = Math.cos(a);
      const s = Math.sin(a);
      out.push([cx + (x - cx) * c - (y - cy) * s, cy + (x - cx) * s + (y - cy) * c]);
    }
    return out;
  }

  const activeColor = () => state.colors[state.slot];

  function useColor(hex) {
    hex = hex.toLowerCase();
    state.colors[state.slot] = hex;
    store.set('colors', state.colors);
    syncColors();
  }

  function remember(hex) {
    state.recent = [hex, ...state.recent.filter((c) => c !== hex)].slice(0, 10);
    store.set('recent', state.recent);
    renderRecent();
  }

  function doFill(x, y) {
    const regions = new Set(mirrored(x, y).map(([px, py]) => board.labelAt(px, py)).filter(Boolean));
    if (!regions.size) return;
    board.begin();
    const style = { mode: state.fill, a: state.colors[0], b: state.colors[1], pattern: state.pattern, scale: state.patternScale };
    for (const r of regions) board.fill(r, style);
    if (board.commit()) changed();
    remember(state.colors[0]);
    if (state.fill !== 'solid') remember(state.colors[1]);
  }

  /* ── Pointer handling: tap to fill, drag to paint, two fingers to zoom ─ */

  const pointers = new Map();
  let gesture = null;
  let stroke = null;
  let tap = null;
  let pan = null;
  let spaceDown = false;

  function brushNow() {
    return { kind: state.brush, size: state.size, color: activeColor(), eraser: state.tool === 'eraser' };
  }

  stage.addEventListener('pointerdown', (e) => {
    if (!board.W || !loadingEl.hidden) return;
    stage.setPointerCapture(e.pointerId);
    stage.focus({ preventScroll: true });
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    closeMenu();

    if (pointers.size === 2) {
      if (stroke) {
        board.abort();
        stroke = null;
      }
      tap = null;
      pan = null;
      const [a, b] = [...pointers.values()];
      gesture = { d: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, s: view.s, tx: view.tx, ty: view.ty };
      return;
    }
    if (pointers.size > 2) return;

    const panning = state.tool === 'hand' || spaceDown || e.button === 1 || e.button === 2;
    if (panning) {
      pan = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty };
      stage.classList.add('is-panning');
      return;
    }
    if (state.tool === 'brush' || state.tool === 'eraser') {
      const [x, y] = toBoard(e);
      board.begin();
      const brush = brushNow();
      stroke = {
        brush,
        copies: mirrored(x, y).map(([px, py]) => {
          const r = state.inside ? board.labelAt(px, py) : 0;
          return { x: px, y: py, m: r ? board.mask(r) : null, skip: state.inside && !r };
        })
      };
      for (const c of stroke.copies) if (!c.skip) board.paint(c.x, c.y, c.x, c.y, brush, c.m);
      return;
    }
    /* Fill and picker act on release, so a drag can pan the page instead. */
    tap = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty, moved: false };
  });

  stage.addEventListener('pointermove', (e) => {
    moveCursor(e);
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (gesture && pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      const r = stage.getBoundingClientRect();
      const ns = clamp(gesture.s * (d / gesture.d), view.fitS * 0.6, view.fitS * 14);
      const ox = gesture.mx - r.left;
      const oy = gesture.my - r.top;
      view.s = ns;
      view.tx = mx - r.left - (ox - gesture.tx) * (ns / gesture.s);
      view.ty = my - r.top - (oy - gesture.ty) * (ns / gesture.s);
      view.fitted = false;
      applyView();
      return;
    }
    if (pan) {
      view.tx = pan.tx + e.clientX - pan.x;
      view.ty = pan.ty + e.clientY - pan.y;
      view.fitted = false;
      applyView();
      return;
    }
    if (stroke) {
      const events = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
      for (const ev of events.length ? events : [e]) {
        const [x, y] = toBoard(ev);
        const pts = mirrored(x, y);
        stroke.copies.forEach((c, i) => {
          const [px, py] = pts[i];
          if (!c.skip) board.paint(c.x, c.y, px, py, stroke.brush, c.m);
          c.x = px;
          c.y = py;
        });
      }
      return;
    }
    if (tap) {
      const dx = e.clientX - tap.x;
      const dy = e.clientY - tap.y;
      if (!tap.moved && Math.hypot(dx, dy) > 7) tap.moved = true;
      if (tap.moved) {
        view.tx = tap.tx + dx;
        view.ty = tap.ty + dy;
        view.fitted = false;
        applyView();
      }
    }
  });

  function endPointer(e) {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    if (gesture) {
      if (pointers.size < 2) gesture = null;
      return;
    }
    if (pan) {
      pan = null;
      stage.classList.remove('is-panning');
      return;
    }
    if (stroke) {
      stroke = null;
      if (board.commit()) changed();
      remember(activeColor());
      return;
    }
    if (tap && e.type === 'pointerup' && !tap.moved) {
      const [x, y] = toBoard(e);
      if (state.tool === 'picker' && (x < 0 || y < 0 || x >= board.W || y >= board.H)) {
        setTip('Tap on the page itself to pick a color from it.');
      } else if (state.tool === 'picker') {
        const hex = board.pick(x, y);
        useColor(hex);
        setTool(state.prevTool || 'fill');
        const slot = $('[data-slot="' + state.slot + '"]', editorEl);
        slot.classList.add('is-picked');
        setTip('Picked ' + hex + ' — it is now your ' + (state.slot ? 'second' : 'first') + ' color.');
        tipTimer = setTimeout(() => {
          slot.classList.remove('is-picked');
          setTip();
        }, 2600);
      } else if (state.tool === 'fill') {
        doFill(x, y);
      }
    }
    tap = null;
  }
  stage.addEventListener('pointerup', endPointer);
  stage.addEventListener('pointercancel', (e) => {
    if (stroke) {
      board.abort();
      stroke = null;
    }
    tap = null;
    endPointer(e);
  });
  stage.addEventListener('contextmenu', (e) => e.preventDefault());

  stage.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const r = stage.getBoundingClientRect();
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomAt(Math.exp(-delta * (e.ctrlKey ? 0.01 : 0.0018)), e.clientX - r.left, e.clientY - r.top);
    },
    { passive: false }
  );

  function moveCursor(e) {
    const show = e.pointerType !== 'touch' && (state.tool === 'brush' || state.tool === 'eraser') && !spaceDown && !pan;
    cursorEl.hidden = !show;
    if (!show) return;
    const r = stage.getBoundingClientRect();
    const d = Math.max(10, state.size * view.s);
    cursorEl.style.width = cursorEl.style.height = d + 'px';
    cursorEl.style.transform = `translate(${e.clientX - r.left - d / 2}px, ${e.clientY - r.top - d / 2}px)`;
  }
  stage.addEventListener('pointerleave', () => (cursorEl.hidden = true));

  /* ── Panel ───────────────────────────────────────────────────────────── */

  const tipEl = $('[data-tip]', editorEl);
  const TIPS = {
    fill: 'Tap an area to fill it. Drag to slide the page.',
    brush: 'Drag to paint. Your stroke stays inside the area you start in.',
    brushFree: 'Drag to paint anywhere on the page.',
    eraser: 'Drag to rub color out.',
    picker: 'Tap a spot on the page to reuse its color.',
    hand: 'Drag to slide the page. Scroll or pinch to zoom.'
  };
  let tipTimer;
  /* On a phone the tip sits over the drawing, so it says its piece and goes. */
  const coarse = matchMedia('(pointer: coarse)').matches;
  function setTip(text) {
    clearTimeout(tipTimer);
    tipEl.textContent = text || (state.tool === 'brush' && !state.inside ? TIPS.brushFree : TIPS[state.tool]);
    if (coarse && !text) tipTimer = setTimeout(() => (tipEl.textContent = ''), 4000);
  }

  function setTool(tool) {
    if (tool === 'picker' && state.tool !== 'picker') state.prevTool = state.tool;
    state.tool = tool;
    for (const b of $$('[data-tool]', editorEl)) b.setAttribute('aria-pressed', String(b.dataset.tool === tool));
    $('[data-for="fill"]', editorEl).hidden = tool !== 'fill';
    $('[data-for="brush"]', editorEl).hidden = !(tool === 'brush' || tool === 'eraser');
    stage.dataset.tool = tool;
    cursorEl.hidden = true;
    setTip();
  }

  function syncColors() {
    $$('[data-slot]', editorEl).forEach((b, i) => {
      b.style.setProperty('--c', state.colors[i]);
      b.setAttribute('aria-pressed', String(state.slot === i));
      b.setAttribute('aria-label', `${i ? 'Second' : 'First'} color, ${state.colors[i]}${state.slot === i ? ' (palette picks go here)' : ''}`);
    });
    $('[data-custom]', editorEl).value = activeColor();
    for (const sw of $$('[data-color]', editorEl)) sw.setAttribute('aria-selected', String(sw.dataset.color === activeColor()));
    renderPatterns();
  }

  function swatch(hex) {
    return `<button type="button" class="nc-swatch" role="option" data-color="${hex}" style="--c:${hex}" aria-label="${hex}" aria-selected="${hex === activeColor()}"></button>`;
  }

  function renderPalette() {
    $('[data-swatches]', editorEl).innerHTML = PALETTES[state.palette].map(swatch).join('');
  }

  function renderRecent() {
    $('[data-recent]', editorEl).innerHTML = state.recent.map(swatch).join('');
    $('[data-recent-label]', editorEl).hidden = !state.recent.length;
  }

  function renderPatterns() {
    const host = $('[data-patterns]', editorEl);
    if (host.hidden) return;
    host.replaceChildren(
      ...Object.keys(PATTERNS).map((kind) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'nc-pattern';
        b.dataset.pattern = kind;
        b.setAttribute('aria-pressed', String(kind === state.pattern));
        b.setAttribute('aria-label', PATTERN_NAMES[kind]);
        b.title = PATTERN_NAMES[kind];
        const [c, x] = canvas(44, 44);
        x.fillStyle = x.createPattern(patternTile(kind, state.colors[0], state.colors[1], 1, 16), 'repeat');
        x.fillRect(0, 0, 44, 44);
        b.append(c);
        return b;
      })
    );
  }

  const HINTS = {
    solid: 'Tap any area to fill it.',
    linear: 'Fades from the first color to the second across the area.',
    radial: 'Glows from the first color in the middle to the second at the edges.',
    pattern: 'Uses both colors. Pick a pattern, then tap an area.'
  };

  function setFill(mode) {
    state.fill = mode;
    store.set('fill', mode);
    for (const b of $$('[data-fill]', editorEl)) b.setAttribute('aria-pressed', String(b.dataset.fill === mode));
    $('[data-patterns]', editorEl).hidden = mode !== 'pattern';
    $('[data-pattern-size]', editorEl).hidden = mode !== 'pattern';
    $('[data-fill-hint]', editorEl).textContent = HINTS[mode];
    renderPatterns();
  }

  function setBrush(kind) {
    state.brush = kind;
    store.set('brush', kind);
    for (const b of $$('[data-brush]', editorEl)) b.setAttribute('aria-pressed', String(b.dataset.brush === kind));
  }

  $('[data-palette-set]', editorEl).innerHTML = Object.keys(PALETTES)
    .map((n) => `<option${n === state.palette ? ' selected' : ''}>${esc(n)}</option>`)
    .join('');

  editorEl.addEventListener('click', (e) => {
    const t = e.target;
    const tool = t.closest('[data-tool]');
    if (tool) return setTool(tool.dataset.tool);
    const sw = t.closest('[data-color]');
    if (sw) return useColor(sw.dataset.color);
    const slot = t.closest('[data-slot]');
    if (slot) {
      state.slot = Number(slot.dataset.slot);
      return syncColors();
    }
    const fillBtn = t.closest('[data-fill]');
    if (fillBtn) return setFill(fillBtn.dataset.fill);
    const pat = t.closest('[data-pattern]');
    if (pat) {
      state.pattern = pat.dataset.pattern;
      store.set('pattern', state.pattern);
      if (state.tool !== 'fill') setTool('fill');
      return renderPatterns();
    }
    const brushBtn = t.closest('[data-brush]');
    if (brushBtn) return setBrush(brushBtn.dataset.brush);
    const act = t.closest('[data-act]');
    if (act) action(act.dataset.act, act);
  });

  editorEl.addEventListener('input', (e) => {
    const t = e.target;
    if (t.matches('[data-custom]')) useColor(t.value);
    else if (t.matches('[data-brush-size]')) {
      state.size = Number(t.value);
      store.set('size', state.size);
    } else if (t.matches('[data-pattern-scale]')) state.patternScale = Number(t.value);
  });

  editorEl.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-custom]')) remember(t.value.toLowerCase());
    else if (t.matches('[data-inside]')) {
      state.inside = t.checked;
      setTip();
    }
    else if (t.matches('[data-symmetry]')) state.sym = Number(t.value);
    else if (t.matches('[data-palette-set]')) {
      state.palette = t.value;
      store.set('palette', state.palette);
      renderPalette();
      syncColors();
    }
  });

  const menuBtn = $('[data-act="menu"]', editorEl);
  const menuList = $('[data-menu-list]', editorEl);
  function closeMenu() {
    menuList.hidden = true;
    menuBtn.setAttribute('aria-expanded', 'false');
  }
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-menu]')) closeMenu();
  });

  const canShareFiles = (() => {
    try {
      return !!navigator.canShare && navigator.canShare({ files: [new File([''], 'x.png', { type: 'image/png' })] });
    } catch {
      return false;
    }
  })();
  $('[data-act="share"]', editorEl).hidden = !canShareFiles;

  function fileName() {
    return `nilya-press-${state.sheet.id}-coloring.png`;
  }

  function finalBlob() {
    return new Promise((resolve) => board.composite(0, 'Colored at nilyapress.com').toBlob(resolve, 'image/png'));
  }

  async function action(name) {
    switch (name) {
      case 'back':
        if (openedFromLibrary && history.state && history.state.nc) history.back();
        else navigate(catHref(state.sheet.cat), { replace: true });
        break;
      case 'undo':
        if (board.step(board.undo, board.redo, 'before')) changed();
        break;
      case 'redo':
        if (board.step(board.redo, board.undo, 'after')) changed();
        break;
      case 'zoom-in':
        zoomCenter(1.35);
        break;
      case 'zoom-out':
        zoomCenter(1 / 1.35);
        break;
      case 'fit':
        fit();
        break;
      case 'swap':
        state.colors.reverse();
        store.set('colors', state.colors);
        syncColors();
        break;
      case 'menu': {
        const open = menuList.hidden;
        menuList.hidden = !open;
        menuBtn.setAttribute('aria-expanded', String(open));
        if (open) menuList.querySelector('button:not([hidden])').focus();
        break;
      }
      case 'download': {
        closeMenu();
        const url = URL.createObjectURL(await finalBlob());
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName();
        document.body.append(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        break;
      }
      case 'share': {
        closeMenu();
        const file = new File([await finalBlob()], fileName(), { type: 'image/png' });
        try {
          await navigator.share({ files: [file], title: `${state.sheet.name} — colored at Nilya Press` });
        } catch {}
        break;
      }
      case 'print-colored':
        closeMenu();
        printImage(URL.createObjectURL(await finalBlob()), true);
        break;
      case 'print-blank':
        closeMenu();
        printImage(state.sheet.src, false);
        break;
      case 'reset':
        closeMenu();
        if (confirm('Clear all the color from this page? You can still undo it.')) {
          board.clear();
          syncHistory();
          clearTimeout(saveTimer);
          board.touched = false;
          const id = state.sheet.id;
          await idb('readwrite', (st) => st.delete(id));
          const old = progress.get(id);
          if (old) URL.revokeObjectURL(old.thumbUrl);
          progress.delete(id);
        }
        break;
    }
  }

  function printImage(src, revoke) {
    const host = document.createElement('div');
    host.className = 'nc-print';
    const img = new Image();
    img.alt = '';
    host.append(img);
    document.body.append(host);
    document.documentElement.classList.add('nc-printing');
    const done = () => {
      document.documentElement.classList.remove('nc-printing');
      host.remove();
      if (revoke) URL.revokeObjectURL(src);
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    img.onload = () => setTimeout(() => window.print(), 50);
    img.src = src;
  }

  function syncHistory() {
    $('[data-act="undo"]', editorEl).disabled = !board.undo.length;
    $('[data-act="redo"]', editorEl).disabled = !board.redo.length;
  }

  let saveTimer;
  function changed() {
    syncHistory();
    clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 900);
  }

  async function save() {
    clearTimeout(saveTimer);
    if (!state.sheet || !board.W || !board.touched) return;
    const id = state.sheet.id;
    const [color, thumb] = await Promise.all([
      new Promise((r) => board.color.toBlob(r, 'image/png')),
      new Promise((r) => board.composite(480).toBlob(r, 'image/jpeg', 0.86))
    ]);
    if (!color || !thumb) return;
    await idb('readwrite', (st) => st.put({ id, w: board.W, h: board.H, color, thumb, updated: Date.now() }));
    const old = progress.get(id);
    if (old) URL.revokeObjectURL(old.thumbUrl);
    progress.set(id, { updated: Date.now(), thumbUrl: URL.createObjectURL(thumb) });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') save();
  });

  /* ── Open and close ──────────────────────────────────────────────────── */

  let loadToken = 0;
  let lastFocus = null;
  let libScroll = 0;
  let restoreScroll = null;

  /* The editor fills what the viewport has left under the site header, so
     the page, the tools and the store's own navigation are all in view. */
  function sizeEditor() {
    if (editorEl.hidden) return;
    const top = editorEl.getBoundingClientRect().top + window.scrollY;
    editorEl.style.setProperty('--nc-editor-h', Math.max(520, Math.round(window.innerHeight - top - 16)) + 'px');
  }
  window.addEventListener('resize', sizeEditor);

  async function openEditor(sheet) {
    if (state.sheet && state.sheet.id === sheet.id && !editorEl.hidden) return;
    if (state.sheet) await save();
    const token = ++loadToken;
    state.sheet = sheet;
    if (editorEl.hidden) lastFocus = document.activeElement;
    document.documentElement.classList.add('nc-editing');
    editorEl.hidden = false;
    $('[data-title]', editorEl).textContent = sheet.name;
    const lv = $('[data-level]', editorEl);
    lv.textContent = LEVELS[sheet.level];
    lv.className = `nc-level nc-level--${sheet.level}`;
    document.title = `${sheet.name} coloring page — ${baseTitle}`;
    loadingEl.hidden = false;
    boardEl.replaceChildren();
    if (restoreScroll === null) libScroll = window.scrollY;
    window.scrollTo(0, 0);
    sizeEditor();
    $('[data-title]', editorEl).focus({ preventScroll: true });
    const saved = await idb('readonly', (st) => st.get(sheet.id));
    try {
      await board.load(sheet, saved);
    } catch (err) {
      if (token === loadToken) loadingEl.querySelector('span:last-child').textContent = 'This page could not be loaded. Please try again.';
      return;
    }
    if (token !== loadToken) return;
    loadingEl.hidden = true;
    syncHistory();
    fit();
  }

  function closeEditor() {
    document.documentElement.classList.remove('nc-editing');
    if (editorEl.hidden) return;
    save();
    loadToken++;
    editorEl.hidden = true;
    state.sheet = null;
    boardEl.replaceChildren();
    board.W = 0;
    restoreScroll = libScroll;
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    loadProgressIndex().then(() => {
      if (editorEl.hidden) route();
    });
  }

  /* ── Keyboard ────────────────────────────────────────────────────────── */

  document.addEventListener('keydown', (e) => {
    if (editorEl.hidden) return;
    const typing = e.target.closest('input, select, textarea');
    if (e.key === 'Escape') {
      closeMenu();
      return;
    }
    /* The site header and footer are on screen too; shortcuts belong to the
       editor only, and never steal Space from a focused button or link. */
    if (!editorEl.contains(e.target) && e.target !== document.body) return;
    if (typing && e.target.type !== 'range' && e.target.type !== 'checkbox') return;
    if (e.key === ' ' && e.target.closest('button, a, select, input')) return;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      action(e.shiftKey ? 'redo' : 'undo');
    } else if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      action('redo');
    } else if (mod) {
      return;
    } else if (e.key === ' ') {
      if (!spaceDown) {
        spaceDown = true;
        stage.classList.add('is-grab');
      }
      e.preventDefault();
    } else if (e.key === '+' || e.key === '=') zoomCenter(1.25);
    else if (e.key === '-' || e.key === '_') zoomCenter(1 / 1.25);
    else if (e.key === '0') fit();
    else if (e.key.toLowerCase() === 'x') action('swap');
    else {
      const tool = { f: 'fill', b: 'brush', e: 'eraser', i: 'picker', h: 'hand' }[e.key.toLowerCase()];
      if (tool) setTool(tool);
    }
  });
  document.addEventListener('keyup', (e) => {
    if (e.key === ' ') {
      spaceDown = false;
      stage.classList.remove('is-grab');
    }
  });

  /* The segmenter is exposed the way nilya-binding.js exposes its maths, so
     shopify-qa/coloring.mjs can check every page in the library divides into
     sensible areas without duplicating it. */
  window.NilyaColoring = { segment, catalog };

  /* ── Start ───────────────────────────────────────────────────────────── */

  $('[data-brush-size]', editorEl).value = state.size;
  renderPalette();
  renderRecent();
  setFill(state.fill);
  setBrush(state.brush);
  setTool('fill');
  syncColors();
  loadProgressIndex().then(route);
})();
